import type { Widget } from '../contract.js';
import { isYou } from './lookup.js';
import type { AppState } from './types.js';

/** Pure facts derived from the state that several modules need (04 §12–§13). */

/** Whether the composer draft differs from what was opened (04 §12.2). */
export const draftDirty = (st: AppState): boolean => !!st.draft && JSON.stringify(st.draft) !== st.draftBase;
/** Whether the profile being edited differs from the saved one (04 §13.1). */
export const peDirty = (st: AppState): boolean => !!st.pe && JSON.stringify(st.pe) !== st.peBase;

/** A text widget with only whitespace counts as empty: never saved, never enough to share. */
export const hasContent = (item: Pick<Widget, 't' | 'text'>): boolean => item.t !== 'text' || (item.text || '').trim().length > 0;

export interface ComposeState { readonly can: boolean; readonly saving: boolean; readonly failed: boolean; readonly dirty: boolean }
/** `composeState`: whether Share / Save can be pressed (04 §12.3). */
export function composeState(st: AppState): ComposeState {
  const items = st.draft?.items ?? [], saving = st.cStatus === 'saving', failed = st.cStatus === 'failed', dirty = draftDirty(st);
  const has = items.some(hasContent);
  return { can: has && !saving && (!st.cEditing || dirty || failed), saving, failed, dirty };
}

export interface EditDoc { readonly kind: 'post' | 'profile'; readonly items: readonly Widget[]; readonly saving: boolean }
/** `doc`: the grid being edited (your post in the composer, or your profile), if any. */
export function editDoc(st: AppState): EditDoc | null {
  if (st.view === 'compose' && st.draft) return { kind: 'post', items: st.draft.items, saving: st.cStatus === 'saving' };
  if (st.view === 'profile' && st.pe && isYou(st, st.profileOf)) return { kind: 'profile', items: st.pe.items, saving: st.peStatus === 'saving' };
  return null;
}
